import { useEffect, useMemo, useState } from "react";
import type { Article, Catalogue, Famille, TotauxReservation } from "@hopitech/contracts";
import { ErreurApi, ajouterLigne, creerReservation, lireCatalogue, validerReservation } from "../lib/api.ts";
import { useApp } from "../app/state.tsx";
import { useRoute } from "../lib/router.tsx";

const euros = (centimes: number): string =>
  (centimes / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

const ETAPES = ["Le stand", "Les options", "Récapitulatif", "Confirmation"] as const;

export function ExposantTunnel(): React.ReactElement {
  const app = useApp();
  const { naviguer } = useRoute();
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const [etape, setEtape] = useState<1 | 2 | 3 | 4>(1);
  const [enseigne, setEnseigne] = useState("");
  const [zone, setZone] = useState("");
  const [standRef, setStandRef] = useState("");
  const [options, setOptions] = useState<Record<string, number>>({});
  const [figees, setFigees] = useState(false);
  const [reference, setReference] = useState("");
  const [totauxServeur, setTotauxServeur] = useState<TotauxReservation | null>(null);
  const [cle, setCle] = useState("");

  useEffect(() => {
    let vivant = true;
    setChargement(true);
    lireCatalogue(app.edition.id)
      .then((c) => { if (vivant) { setCatalogue(c); setChargement(false); } })
      .catch((e: unknown) => { if (vivant) { setErreur(e instanceof ErreurApi ? e.message : "Catalogue indisponible"); setChargement(false); } });
    return () => { vivant = false; };
  }, [app.edition.id]);

  const parRef = useMemo(() => new Map((catalogue?.articles ?? []).map((a) => [a.reference, a])), [catalogue]);
  const stands = (catalogue?.articles ?? []).filter((a) => a.famille === "stand");
  const optionsCatalogue = (catalogue?.articles ?? []).filter((a) => a.famille !== "stand");

  const lignesChoisies = useMemo(() => {
    const l: { article: Article; quantite: number }[] = [];
    const stand = parRef.get(standRef);
    if (stand) l.push({ article: stand, quantite: 1 });
    for (const [ref, q] of Object.entries(options)) {
      const a = parRef.get(ref);
      if (a && q > 0) l.push({ article: a, quantite: q });
    }
    return l;
  }, [parRef, standRef, options]);

  const totauxIndicatifs = useMemo<TotauxReservation>(() => {
    let ht = 0, tva = 0;
    for (const { article, quantite } of lignesChoisies) {
      const ligneHt = article.prixHtCentimes * quantite;
      ht += ligneHt;
      tva += Math.round((ligneHt * article.tauxTva) / 100);
    }
    return { htCentimes: ht, tvaCentimes: tva, ttcCentimes: ht + tva };
  }, [lignesChoisies]);

  async function creer(): Promise<void> {
    setErreur(null); setEnCours(true);
    try {
      const r = await creerReservation({
        edition: app.edition.id,
        enseigne: enseigne.trim(),
        souhaitPlacement: { referenceStand: standRef, ...(zone.trim() ? { preferenceZone: zone.trim() } : {}) },
      });
      setReference(r.reference);
      setEtape(2);
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnCours(false); }
  }

  async function figerOptions(): Promise<void> {
    if (figees) { setEtape(3); return; }
    setErreur(null); setEnCours(true);
    try {
      for (const [ref, q] of Object.entries(options)) {
        const a = parRef.get(ref);
        if (a && q > 0) await ajouterLigne(reference, { referenceArticle: ref, famille: a.famille as Famille, quantite: q });
      }
      setFigees(true);
      setEtape(3);
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnCours(false); }
  }

  async function valider(): Promise<void> {
    setErreur(null); setEnCours(true);
    try {
      const v = await validerReservation(reference);
      setTotauxServeur(v.totaux);
      setCle(v.cleIdempotence);
      setEtape(4);
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnCours(false); }
  }

  if (chargement) return <Bloc><p style={{ color: "var(--muted)" }}>Chargement du catalogue…</p></Bloc>;

  return (
    <div style={{ maxWidth: 940, display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 24, alignItems: "start" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <Progression etape={etape} />
        {erreur && <div className="pill pill-danger" style={{ alignSelf: "flex-start", textTransform: "none", fontFamily: "var(--sans)", fontSize: 12.5 }}>{erreur}</div>}

        {etape === 1 && (
          <Carte titre="Votre stand" sous="Vous choisissez le type et la surface. L'emplacement précis est attribué ensuite par le bureau.">
            <div className="field">
              <label>Type de stand</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {stands.map((s) => (
                  <label key={s.reference} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", border: `1px solid ${standRef === s.reference ? "var(--accent)" : "var(--line-2)"}`, borderRadius: 10, cursor: "pointer", background: standRef === s.reference ? "var(--soft)" : "var(--surface)" }}>
                    <input type="radio" name="stand" checked={standRef === s.reference} onChange={() => setStandRef(s.reference)} />
                    <span style={{ flex: 1, fontWeight: 600 }}>{s.libelle}</span>
                    <span className="mono num" style={{ color: "var(--muted)" }}>{euros(s.prixHtCentimes)} HT</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="field">
              <label htmlFor="enseigne">Nom de l'enseigne</label>
              <input id="enseigne" className="input" value={enseigne} onChange={(e) => setEnseigne(e.target.value)} placeholder="Nom affiché sur le stand" />
            </div>
            <div className="field">
              <label htmlFor="zone">Souhait de placement <span style={{ color: "var(--faint-2)", fontWeight: 400 }}>(facultatif)</span></label>
              <input id="zone" className="input" value={zone} onChange={(e) => setZone(e.target.value)} placeholder="« proche entrée », « pas à côté de tel concurrent »…" />
              <span className="hint">Une intention, pas une réservation d'emplacement.</span>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="btn btn-primary" disabled={!standRef || !enseigne.trim() || enCours} onClick={() => void creer()}>{enCours ? "…" : "Continuer"}</button>
            </div>
          </Carte>
        )}

        {etape === 2 && (
          <Carte titre="Vos options" sous={figees ? "Options enregistrées." : "Publicités, encarts, repas… ajoutez ce dont vous avez besoin."}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {optionsCatalogue.map((o) => {
                const q = options[o.reference] ?? 0;
                return (
                  <div key={o.reference} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", border: "1px solid var(--line-2)", borderRadius: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{o.libelle}</div>
                      <div className="mono num" style={{ fontSize: 12, color: "var(--muted)" }}>{euros(o.prixHtCentimes)} HT · TVA {o.tauxTva}%</div>
                    </div>
                    <Stepper valeur={q} disabled={figees} onChange={(v) => setOptions((s) => ({ ...s, [o.reference]: v }))} />
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button className="btn btn-ghost" onClick={() => setEtape(1)}>Précédent</button>
              <button className="btn btn-primary" disabled={enCours} onClick={() => void figerOptions()}>{enCours ? "…" : "Continuer"}</button>
            </div>
          </Carte>
        )}

        {etape === 3 && (
          <Carte titre="Récapitulatif" sous="Vérifiez avant de valider. Les prix seront figés à la validation.">
            <div className="scroll-x">
              <table className="table">
                <thead><tr><th>Ligne</th><th>Qté</th><th style={{ textAlign: "right" }}>HT</th></tr></thead>
                <tbody>
                  {lignesChoisies.map(({ article, quantite }) => (
                    <tr key={article.reference}>
                      <td>{article.libelle}</td>
                      <td className="num">{quantite}</td>
                      <td className="num" style={{ textAlign: "right" }}>{euros(article.prixHtCentimes * quantite)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button className="btn btn-ghost" onClick={() => setEtape(2)}>Précédent</button>
              <button className="btn btn-primary" disabled={enCours} onClick={() => void valider()}>{enCours ? "Validation…" : "Valider la réservation"}</button>
            </div>
          </Carte>
        )}

        {etape === 4 && totauxServeur && (
          <Carte titre="Réservation validée" sous="Votre acte est enregistré. Commande Sellsy et badge suivront.">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span className="pill pill-ok">Validée</span>
              <span className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>{reference}</span>
            </div>
            <TotauxVue titre="Total facturé" totaux={totauxServeur} />
            <div className="hint">Clé d'idempotence : <span className="mono">{cle}</span></div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="btn btn-primary" onClick={() => naviguer("/exposant")}>Voir mon espace</button>
            </div>
          </Carte>
        )}
      </div>

      <aside className="card" style={{ padding: 16, position: "sticky", top: 96, display: "flex", flexDirection: "column", gap: 12 }}>
        <span className="eyebrow">Votre panier</span>
        {lignesChoisies.length === 0 ? (
          <p style={{ color: "var(--muted-2)", fontSize: 13, margin: 0 }}>Sélectionnez un stand pour commencer.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {lignesChoisies.map(({ article, quantite }) => (
              <div key={article.reference} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 13 }}>
                <span style={{ color: "var(--ink-2)" }}>{article.libelle}{quantite > 1 ? ` ×${quantite}` : ""}</span>
                <span className="mono num" style={{ color: "var(--muted)" }}>{euros(article.prixHtCentimes * quantite)}</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ borderTop: "1px solid var(--line)", paddingTop: 10 }}>
          <TotauxVue totaux={totauxServeur ?? totauxIndicatifs} compact indicatif={!totauxServeur} />
        </div>
      </aside>
    </div>
  );
}

function messageErreur(e: unknown): string {
  return e instanceof ErreurApi ? e.message : "Une erreur est survenue. Réessayez.";
}

function Bloc({ children }: { children: React.ReactNode }): React.ReactElement {
  return <div className="card" style={{ padding: 24, maxWidth: 640 }}>{children}</div>;
}

function Carte({ titre, sous, children }: { titre: string; sous: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div className="card" style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <h2 className="display" style={{ fontSize: 18, margin: "0 0 2px" }}>{titre}</h2>
        <p style={{ margin: 0, color: "var(--muted-2)", fontSize: 13 }}>{sous}</p>
      </div>
      {children}
    </div>
  );
}

function Progression({ etape }: { etape: number }): React.ReactElement {
  return (
    <div style={{ display: "flex", gap: 8 }}>
      {ETAPES.map((nom, i) => {
        const n = i + 1;
        const actif = n === etape;
        const passe = n < etape;
        return (
          <div key={nom} style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ height: 4, borderRadius: 999, background: actif || passe ? "var(--accent)" : "var(--line-2)" }} />
            <span style={{ fontSize: 11.5, fontWeight: actif ? 700 : 500, color: actif ? "var(--accent)" : "var(--muted-2)" }}>{n}. {nom}</span>
          </div>
        );
      })}
    </div>
  );
}

function Stepper({ valeur, disabled, onChange }: { valeur: number; disabled?: boolean; onChange: (v: number) => void }): React.ReactElement {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <button className="btn btn-ghost" style={{ padding: "4px 10px" }} disabled={disabled || valeur <= 0} onClick={() => onChange(Math.max(0, valeur - 1))}>−</button>
      <span className="num" style={{ minWidth: 18, textAlign: "center", fontWeight: 600 }}>{valeur}</span>
      <button className="btn btn-ghost" style={{ padding: "4px 10px" }} disabled={disabled} onClick={() => onChange(valeur + 1)}>+</button>
    </div>
  );
}

function TotauxVue({ totaux, titre, compact, indicatif }: { totaux: TotauxReservation; titre?: string; compact?: boolean; indicatif?: boolean }): React.ReactElement {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {titre && <span className="eyebrow">{titre}</span>}
      <Ligne k="Total HT" v={euros(totaux.htCentimes)} />
      <Ligne k="TVA" v={euros(totaux.tvaCentimes)} />
      <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: compact ? 15 : 17 }} className="display">
        <span>TTC</span><span className="num">{euros(totaux.ttcCentimes)}</span>
      </div>
      {indicatif && <span className="hint">Montants indicatifs, figés à la validation.</span>}
    </div>
  );
}

function Ligne({ k, v }: { k: string; v: string }): React.ReactElement {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--muted)" }}>
      <span>{k}</span><span className="num">{v}</span>
    </div>
  );
}
